import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionYesNoField, QuestionSocialInsuranceNumberField } from 'components/Questionnaires/fields';
import { validateQuestion, requiredValidator, lengthValidator, sequentialIntegerValidator, sameCharacterValidator } from 'components/Questionnaires/validators';

export const QuestionCanadianTaxResidentBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [isCanadianTaxResident, setIsCanadianTaxResident] = useState({
    id: 'isCanadianTaxResident',
    label: 'questionnaire.rc518.canadianTaxResident.isCanadianTaxResident.label',
    component: QuestionYesNoField,
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.canadianTaxResident.isCanadianTaxResident.errorMessage' }
      ]
    },
    answer: answer?.isCanadianTaxResident
  });

  const [socialInsuranceNumber, setSocialInsuranceNumber] = useState({
    id: 'socialInsuranceNumber',
    label: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.label',
    component: QuestionSocialInsuranceNumberField,
    componentProps: {
      length: 9
    },
    visible: true,
    info: {
      instructions: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.info'
    },
    validation: {
      conditions: [
        { condition: lengthValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.errorMessage' },
        { condition: sequentialIntegerValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.sequentialErrorMessage' },
        { condition: sameCharacterValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.sameCharErrorMessage' }
      ]
    },
    answer: answer?.socialInsuranceNumber
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => isCanadianTaxResident.isValid;

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'isCanadianTaxResident': {
        const newAnswer = { ...q, answer: value };
        validateQuestion(newAnswer);
        setIsCanadianTaxResident(newAnswer);
        setAnswer({ ...answer, isCanadianTaxResident: value });
        break;
      }
      case 'socialInsuranceNumber': {
        const newAnswer = { ...q, answer: value };
        validateQuestion(newAnswer);
        setSocialInsuranceNumber(newAnswer);
        setAnswer({ ...answer, socialInsuranceNumber: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      if (Object.keys(answer).length > 0 && answer.constructor === Object) {
        dispatch(answerBlock(block.id, answer, isValid()));
      } else {
        onQuestionChange(isCanadianTaxResident, undefined);
        onQuestionChange(socialInsuranceNumber, undefined);
      }
    } else {
      onAnswer(block, answer, isValid());
    }
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.isCanadianTaxResident) {
        onQuestionChange({ ...isCanadianTaxResident, isForceEdited: true }, '');
      }
      if (!answer?.socialInsuranceNumber) {
        onQuestionChange({ ...socialInsuranceNumber, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ isCanadianTaxResident.id } blockId={ blockIdentifier } question={ isCanadianTaxResident } { ...isCanadianTaxResident.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ socialInsuranceNumber.id } blockId={ blockIdentifier } question={ socialInsuranceNumber } { ...socialInsuranceNumber.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
  </React.Fragment>);
};

QuestionCanadianTaxResidentBlock.displayName = 'QuestionCanadianTaxResidentBlock';

QuestionCanadianTaxResidentBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionCanadianTaxResidentBlock.defaultProps = {
  onAnswer: null
};

export default QuestionCanadianTaxResidentBlock;
